// src/pages/TimeTrackingPage.js
import React, { useState, useEffect } from 'react';
import { 
  Clock, 
  Play, 
  Square, 
  Filter,
  ChevronDown,
  Plus,
  Users,
  Edit,
  Trash2,
  Calendar,
  Clock4,
  CheckCircle2,
  Car,
  AlignLeft,
  Search,
  Briefcase
} from 'lucide-react';
import { projectsAPI, timeEntriesAPI } from '../services/api';
import './TimeTrackingPage.css'; // CSS für Animationen

const TimeTrackingPage = () => {
  const [projects, setProjects] = useState([]);
  const [activeEntry, setActiveEntry] = useState(null);
  const [selectedProject, setSelectedProject] = useState('');
  const [description, setDescription] = useState('');
  const [timeEntries, setTimeEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showTravelAllowanceModal, setShowTravelAllowanceModal] = useState(false);
  const [travelAllowance, setTravelAllowance] = useState(0);
  const [employeeCount, setEmployeeCount] = useState(1); // Neue State-Variable für Mitarbeiteranzahl
  const [isProjectDropdownOpen, setIsProjectDropdownOpen] = useState(false);

  // Neue States für die manuelle Zeiterfassung
  const [showManualEntryModal, setShowManualEntryModal] = useState(false);
  const [manualEntry, setManualEntry] = useState({
    date: new Date().toISOString().split('T')[0],
    startTime: '',
    endTime: '',
    project: '',
    description: '',
    travelAllowance: 0,
    employee_count: 1 // Standard: 1 Mitarbeiter
  });

  // Neue States für die Bearbeitung von Zeiteinträgen
  const [showEditModal, setShowEditModal] = useState(false);
  const [editEntry, setEditEntry] = useState({
    id: null,
    date: '',
    startTime: '',
    endTime: '',
    project_id: '',
    description: '',
    travel_allowance: 0,
    employee_count: 1
  });

  // Projekte und aktive Zeiterfassung laden
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [projectsResponse, timeEntriesResponse] = await Promise.all([
          projectsAPI.getAll(),
          timeEntriesAPI.getByUser()
        ]);

        setProjects(projectsResponse.data.projects);
        setTimeEntries(timeEntriesResponse.data.timeEntries);

        // Prüfen, ob eine aktive Zeiterfassung besteht
        const active = timeEntriesResponse.data.timeEntries.find(entry => !entry.end_time);
        if (active) {
          setActiveEntry(active);
          setSelectedProject(active.project_id);
          setEmployeeCount(active.employee_count || 1); // Mitarbeiteranzahl setzen
        }
      } catch (err) {
        console.error('Fehler beim Laden der Daten:', err);
        setError('Fehler beim Laden der Daten');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const startTracking = async () => {
    if (!selectedProject) {
      setError('Bitte wähle einen Auftrag aus');
      return;
    }

    try {
      const response = await timeEntriesAPI.start({
        project_id: selectedProject,
        description,
        employee_count: employeeCount // Mitarbeiteranzahl übergeben
      });

      setActiveEntry(response.data.timeEntry);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Starten der Zeiterfassung');
    }
  };

  const stopTracking = async () => {
    if (!activeEntry) return;
    setShowTravelAllowanceModal(true);
  };

  const confirmStopTracking = async () => {
    try {
      await timeEntriesAPI.stop(activeEntry.id, {
        travel_allowance: travelAllowance,
        employee_count: employeeCount // Mitarbeiteranzahl übergeben
      });
      
      // Aktive Zeiterfassung aktualisieren
      setActiveEntry(null);
      setSelectedProject('');
      setDescription('');
      setTravelAllowance(0);
      setEmployeeCount(1);
      setShowTravelAllowanceModal(false);
      
      // Zeiterfassungen neu laden
      const timeEntriesResponse = await timeEntriesAPI.getByUser();
      setTimeEntries(timeEntriesResponse.data.timeEntries);
      
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Stoppen der Zeiterfassung');
      setShowTravelAllowanceModal(false);
    }
  };

  // Zeitzonenbewusste Funktion zur korrekten Umwandlung von lokaler Zeit
  const convertLocalToUTC = (dateString, timeString) => {
    // Erstelle ein Datum im lokalen Format ohne Zeitzonenverschiebung
    // Wir erstellen ein Date-Objekt mit dem Format "YYYY-MM-DDThh:mm:00"
    // Wichtig: KEIN "Z" am Ende, um zu vermeiden, dass die Zeit als UTC interpretiert wird
    const localDateTimeStr = `${dateString}T${timeString}:00`;
    
    // Erstelle ein Date-Objekt aus dem lokalen String
    const localDate = new Date(localDateTimeStr);
    
    // Extrahiere die lokalen Komponenten (ohne Zeitzonenumwandlung)
    const year = localDate.getFullYear();
    const month = String(localDate.getMonth() + 1).padStart(2, '0');
    const day = String(localDate.getDate()).padStart(2, '0');
    const hours = String(localDate.getHours()).padStart(2, '0');
    const minutes = String(localDate.getMinutes()).padStart(2, '0');
    
    // Baue eine neue ISO-String ohne Z-Suffix
    return `${year}-${month}-${day}T${hours}:${minutes}:00`;
  };

  // Neue Funktion für manuelle Zeiterfassung
  const handleManualTimeEntry = async () => {
    if (!manualEntry.project) {
      setError('Bitte wähle einen Auftrag aus');
      return;
    }

    if (!manualEntry.date || !manualEntry.startTime || !manualEntry.endTime) {
      setError('Bitte gib Datum, Start- und Endzeit an');
      return;
    }

    try {
      // Direkte Übergabe der Datums- und Zeitstrings ohne Konvertierung
      await timeEntriesAPI.create({
        project_id: manualEntry.project,
        start_time: convertLocalToUTC(manualEntry.date, manualEntry.startTime),
        end_time: convertLocalToUTC(manualEntry.date, manualEntry.endTime),
        description: manualEntry.description,
        travel_allowance: manualEntry.travelAllowance,
        employee_count: manualEntry.employee_count
      });

      // Schließe Modal und lade Zeiteinträge neu
      setShowManualEntryModal(false);
      setManualEntry({
        date: new Date().toISOString().split('T')[0],
        startTime: '',
        endTime: '',
        project: '',
        description: '',
        travelAllowance: 0,
        employee_count: 1
      });
      
      // Lade Zeiteinträge neu
      const timeEntriesResponse = await timeEntriesAPI.getByUser();
      setTimeEntries(timeEntriesResponse.data.timeEntries);
      
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Erstellen des manuellen Zeiteintrags');
    }
  };

  // Neue Funktion zum Öffnen des Edit-Modals
  const handleEditEntry = (entry) => {
    try {
      let startHour, startMinute, endHour, endMinute, dateValue;
      
      // Versuchen Sie, die Werte direkt aus dem ISO-String zu extrahieren
      if (entry.start_time) {
        const parts = entry.start_time.split('T');
        if (parts.length === 2) {
          dateValue = parts[0];
          const timeParts = parts[1].split(':');
          if (timeParts.length >= 2) {
            startHour = timeParts[0];
            startMinute = timeParts[1];
          }
        }
      }
      
      if (entry.end_time) {
        const parts = entry.end_time.split('T');
        if (parts.length === 2) {
          const timeParts = parts[1].split(':');
          if (timeParts.length >= 2) {
            endHour = timeParts[0];
            endMinute = timeParts[1];
          }
        }
      }
      
      // Falls direkte Extraktion fehlschlägt, Fallback zur alten Methode
      if (!startHour || !startMinute || !dateValue) {
        const startDate = new Date(entry.start_time);
        dateValue = startDate.toISOString().split('T')[0];
        startHour = startDate.getHours().toString().padStart(2, '0');
        startMinute = startDate.getMinutes().toString().padStart(2, '0');
      }
      
      if (entry.end_time && (!endHour || !endMinute)) {
        const endDate = new Date(entry.end_time);
        endHour = endDate.getHours().toString().padStart(2, '0');
        endMinute = endDate.getMinutes().toString().padStart(2, '0');
      }
      
      setEditEntry({
        id: entry.id,
        date: dateValue,
        startTime: `${startHour}:${startMinute}`,
        endTime: entry.end_time ? `${endHour}:${endMinute}` : '',
        project_id: entry.project_id,
        description: entry.description || '',
        travel_allowance: parseFloat(entry.travel_allowance) || 0,
        employee_count: entry.employee_count || 1
      });
      
      setShowEditModal(true);
    } catch (error) {
      console.error('Fehler beim Bearbeiten des Eintrags:', error);
      
      // Fallback zur alten Methode bei Fehlern
      const startDate = new Date(entry.start_time);
      const endDate = entry.end_time ? new Date(entry.end_time) : null;
      
      const startHour = startDate.getHours().toString().padStart(2, '0');
      const startMinute = startDate.getMinutes().toString().padStart(2, '0');
      const endHour = endDate ? endDate.getHours().toString().padStart(2, '0') : '';
      const endMinute = endDate ? endDate.getMinutes().toString().padStart(2, '0') : '';
      
      setEditEntry({
        id: entry.id,
        date: startDate.toISOString().split('T')[0],
        startTime: `${startHour}:${startMinute}`,
        endTime: endDate ? `${endHour}:${endMinute}` : '',
        project_id: entry.project_id,
        description: entry.description || '',
        travel_allowance: parseFloat(entry.travel_allowance) || 0,
        employee_count: entry.employee_count || 1
      });
      
      setShowEditModal(true);
    }
  };

  // Neue Funktion zum Speichern des bearbeiteten Zeiteintrags
  const handleUpdateTimeEntry = async () => {
    if (!editEntry.project_id) {
      setError('Bitte wähle einen Auftrag aus');
      return;
    }

    if (!editEntry.date || !editEntry.startTime || !editEntry.endTime) {
      setError('Bitte gib Datum, Start- und Endzeit an');
      return;
    }

    try {
      // Direkte Übergabe der Datums- und Zeitstrings ohne Konvertierung
      await timeEntriesAPI.update(editEntry.id, {
        project_id: editEntry.project_id,
        start_time: convertLocalToUTC(editEntry.date, editEntry.startTime),
        end_time: convertLocalToUTC(editEntry.date, editEntry.endTime),
        description: editEntry.description,
        travel_allowance: editEntry.travel_allowance,
        employee_count: editEntry.employee_count
      });

      // Schließe Modal und lade Zeiteinträge neu
      setShowEditModal(false);
      
      // Lade Zeiteinträge neu
      const timeEntriesResponse = await timeEntriesAPI.getByUser();
      setTimeEntries(timeEntriesResponse.data.timeEntries);
      
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Aktualisieren des Zeiteintrags');
    }
  };

  // Zeitformat für die Anzeige
  const formatTime = (dateString) => {
    if (!dateString) return '';
    
    try {
      // Direkte Extraktion der Zeit aus dem ISO-String ohne Zeitzonenumwandlung
      const timePart = dateString.split('T')[1];
      if (timePart) {
        // Format: HH:MM:SS -> HH:MM
        return timePart.substr(0, 5);
      }
    } catch (e) {
      console.error('Fehler beim Formatieren der Zeit:', e);
    }
    
    // Fallback zur alten Methode, falls etwas schiefgeht
    const date = new Date(dateString);
    return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  };

  // Datum formatieren
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    try {
      // Direkte Extraktion des Datums aus dem ISO-String ohne Zeitzonenumwandlung
      const datePart = dateString.split('T')[0]; // Format: YYYY-MM-DD
      if (datePart) {
        const [year, month, day] = datePart.split('-');
        return `${day}.${month}.${year}`;
      }
    } catch (e) {
      console.error('Fehler beim Formatieren des Datums:', e);
    }
    
    // Fallback zur alten Methode, falls etwas schiefgeht
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE');
  };

  // Gefilterte Zeiteinträge basierend auf ausgewähltem Projekt
  const filteredTimeEntries = selectedProject 
    ? timeEntries.filter(entry => entry.project_id === selectedProject)
    : timeEntries;

  // Neue Funktion zur Korrektur der UTC-Zeitverschiebung
  const adjustForTimezone = (dateTimeString) => {
    // Diese Funktion ist nicht mehr nötig, da wir die Zeiten ohne UTC-Umwandlung speichern
    const date = new Date(dateTimeString);
    return date.toISOString().replace('Z', '');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gradient-to-b from-gray-50 to-gray-100">
        <div className="animate-fadeIn text-center">
          <Clock className="h-12 w-12 text-primary-600 animate-spin mx-auto" />
          <p className="mt-3 text-gray-600">Daten werden geladen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-b from-gray-50 to-gray-100 min-h-screen p-4 sm:p-8">
      <div className="container mx-auto max-w-7xl">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-6 flex items-center">
          <Clock className="h-6 w-6 mr-2 text-primary-600" />
          Zeiterfassung
        </h1>
        
        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-r animate-fadeIn">
            <div className="flex items-center">
              <div className="flex-shrink-0 text-red-500">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <p className="ml-3 text-sm text-red-700">{error}</p>
            </div>
          </div>
        )}
        
        {/* Modal für Anfahrtspauschale und Mitarbeiteranzahl */}
        {showTravelAllowanceModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 backdrop-blur-sm transition-opacity duration-300">
            <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full transform motion-safe:animate-fadeIn">
              <h2 className="text-xl font-semibold mb-4 text-gray-800 flex items-center">
                <CheckCircle2 className="h-5 w-5 mr-2 text-primary-600" />
                Arbeitsende
              </h2>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <Car className="h-4 w-4 mr-2 text-gray-500" />
                  Anfahrtspauschale (CHF)
                </label>
                <input
                  type="number"
                  value={travelAllowance}
                  onChange={(e) => setTravelAllowance(parseFloat(e.target.value) || 0)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  placeholder="Anfahrtspauschale eingeben"
                />
              </div>
              
              <div className="mb-6">
                <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                  <Users className="h-4 w-4 mr-2 text-gray-500" />
                  Anzahl Mitarbeiter
                </label>
                <input
                  type="number"
                  min="1"
                  value={employeeCount}
                  onChange={(e) => setEmployeeCount(parseInt(e.target.value) || 1)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 transition-colors"
                />
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowTravelAllowanceModal(false);
                    setTravelAllowance(0);
                  }}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-md text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
                >
                  Abbrechen
                </button>
                <button
                  onClick={confirmStopTracking}
                  className="px-4 py-2 bg-primary-600 text-white rounded-md shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
                >
                  Speichern
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Manuelles Zeiterfassung-Modal */}
        {showManualEntryModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 backdrop-blur-sm transition-opacity duration-300">
            <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full transform motion-safe:animate-fadeIn">
              <h2 className="text-xl font-semibold mb-4 text-gray-800 flex items-center">
                <Plus className="h-5 w-5 mr-2 text-primary-600" />
                Manuelle Zeiterfassung
              </h2>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <Calendar className="h-4 w-4 mr-2 text-gray-500" />
                  Datum
                </label>
                <input
                  type="date"
                  value={manualEntry.date}
                  onChange={(e) => setManualEntry({...manualEntry, date: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 transition-colors"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <Clock className="h-4 w-4 mr-2 text-gray-500" />
                    Startzeit
                  </label>
                  <input
                    type="time"
                    value={manualEntry.startTime}
                    onChange={(e) => setManualEntry({...manualEntry, startTime: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <Clock4 className="h-4 w-4 mr-2 text-gray-500" />
                    Endzeit
                  </label>
                  <input
                    type="time"
                    value={manualEntry.endTime}
                    onChange={(e) => setManualEntry({...manualEntry, endTime: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  />
                </div>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Auftrag</label>
                <select
                  value={manualEntry.project}
                  onChange={(e) => setManualEntry({...manualEntry, project: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 transition-colors"
                >
                  <option value="">-- Auftrag auswählen --</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.title}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <AlignLeft className="h-4 w-4 mr-2 text-gray-500" />
                  Beschreibung (optional)
                </label>
                <input
                  type="text"
                  value={manualEntry.description}
                  onChange={(e) => setManualEntry({...manualEntry, description: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  placeholder="Was wurde erledigt?"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4 mb-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <Users className="h-4 w-4 mr-2 text-gray-500" />
                    Anzahl Mitarbeiter
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={manualEntry.employee_count}
                    onChange={(e) => setManualEntry({...manualEntry, employee_count: parseInt(e.target.value) || 1})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <Car className="h-4 w-4 mr-2 text-gray-500" />
                    Anfahrtspauschale (CHF)
                  </label>
                  <input
                    type="number"
                    value={manualEntry.travelAllowance}
                    onChange={(e) => setManualEntry({...manualEntry, travelAllowance: parseFloat(e.target.value) || 0})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    placeholder="0.00"
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowManualEntryModal(false)}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-md text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleManualTimeEntry}
                  className="px-4 py-2 bg-primary-600 text-white rounded-md shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
                >
                  Speichern
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Bearbeitungs-Modal */}
        {showEditModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 backdrop-blur-sm transition-opacity duration-300">
            <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full transform motion-safe:animate-fadeIn">
              <h2 className="text-xl font-semibold mb-4 text-gray-800 flex items-center">
                <Edit className="h-5 w-5 mr-2 text-primary-600" />
                Zeiteintrag bearbeiten
              </h2>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <Calendar className="h-4 w-4 mr-2 text-gray-500" />
                  Datum
                </label>
                <input
                  type="date"
                  value={editEntry.date}
                  onChange={(e) => setEditEntry({...editEntry, date: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 transition-colors"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <Clock className="h-4 w-4 mr-2 text-gray-500" />
                    Startzeit
                  </label>
                  <input
                    type="time"
                    value={editEntry.startTime}
                    onChange={(e) => setEditEntry({...editEntry, startTime: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <Clock4 className="h-4 w-4 mr-2 text-gray-500" />
                    Endzeit
                  </label>
                  <input
                    type="time"
                    value={editEntry.endTime}
                    onChange={(e) => setEditEntry({...editEntry, endTime: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  />
                </div>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Auftrag</label>
                <select
                  value={editEntry.project_id}
                  onChange={(e) => setEditEntry({...editEntry, project_id: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 transition-colors"
                >
                  <option value="">-- Auftrag auswählen --</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.title}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <AlignLeft className="h-4 w-4 mr-2 text-gray-500" />
                  Beschreibung (optional)
                </label>
                <input
                  type="text"
                  value={editEntry.description}
                  onChange={(e) => setEditEntry({...editEntry, description: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  placeholder="Was wurde erledigt?"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4 mb-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <Users className="h-4 w-4 mr-2 text-gray-500" />
                    Anzahl Mitarbeiter
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={editEntry.employee_count}
                    onChange={(e) => setEditEntry({...editEntry, employee_count: parseInt(e.target.value) || 1})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <Car className="h-4 w-4 mr-2 text-gray-500" />
                    Anfahrtspauschale (CHF)
                  </label>
                  <input
                    type="number"
                    value={editEntry.travel_allowance}
                    onChange={(e) => setEditEntry({...editEntry, travel_allowance: parseFloat(e.target.value) || 0})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    placeholder="0.00"
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-md text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleUpdateTimeEntry}
                  className="px-4 py-2 bg-primary-600 text-white rounded-md shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
                >
                  Aktualisieren
                </button>
              </div>
            </div>
          </div>
        )}
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Zeit erfassen Bereich */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow card-shadow">
            <div className="flex justify-between items-center mb-5 pb-3 border-b border-gray-100">
              <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                <Clock className="h-5 w-5 mr-2 text-primary-600" />
                Zeit erfassen
              </h2>
              <button
                onClick={() => setShowManualEntryModal(true)}
                className="flex items-center text-sm px-3 py-1.5 bg-primary-50 text-primary-600 rounded hover:bg-primary-100 transition-colors focus-ring"
              >
                <Plus className="h-4 w-4 mr-1" />
                Manuell eintragen
              </button>
            </div>
            
            {activeEntry ? (
              <div className="bg-primary-50 rounded-lg p-4 mb-4 border-l-4 border-primary-500 hover-lift active-time-entry">
                <div className="mb-5">
                  <p className="text-sm mb-2 font-medium text-gray-700 flex items-center">
                    <Briefcase className="h-4 w-4 mr-2 text-primary-600" />
                    <span className="font-medium">Aktiver Auftrag:</span> 
                    <span className="ml-2 font-bold text-primary-700">{
                      projects.find(p => p.id === activeEntry.project_id)?.title || 'Lädt...'
                    }</span>
                  </p>
                  <p className="text-sm mb-2 font-medium text-gray-700 flex items-center">
                    <AlignLeft className="h-4 w-4 mr-2 text-primary-600" />
                    <span className="font-medium">Beschreibung:</span> 
                    <span className="ml-2">{activeEntry.description || '-'}</span>
                  </p>
                  <p className="text-sm mb-2 font-medium text-gray-700 flex items-center">
                    <Clock className="h-4 w-4 mr-2 text-primary-600" />
                    <span className="font-medium">Gestartet:</span> 
                    <span className="ml-2">{formatDate(activeEntry.start_time)} um {formatTime(activeEntry.start_time)}</span>
                  </p>
                  <p className="text-sm font-medium text-gray-700 flex items-center">
                    <Users className="h-4 w-4 mr-2 text-primary-600" />
                    <span className="font-medium">Mitarbeiter:</span> 
                    <span className="ml-2">{activeEntry.employee_count || 1}</span>
                  </p>
                </div>
                
                <button
                  onClick={stopTracking}
                  className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors flex items-center justify-center w-full sm:w-auto"
                >
                  <Square className="h-4 w-4 mr-2" />
                  Zeiterfassung stoppen
                </button>
              </div>
            ) : (
              <div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Auftrag</label>
                  <div className="relative">
                    <div 
                      onClick={() => setIsProjectDropdownOpen(!isProjectDropdownOpen)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md flex justify-between items-center cursor-pointer bg-white hover:border-primary-400 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <span className={selectedProject ? 'text-gray-900' : 'text-gray-500'}>
                        {selectedProject 
                          ? projects.find(p => p.id === selectedProject)?.title 
                          : '-- Auftrag auswählen --'}
                      </span>
                      <ChevronDown className={`h-5 w-5 text-gray-500 transition-transform ${isProjectDropdownOpen ? 'transform rotate-180' : ''}`} />
                    </div>
                    {isProjectDropdownOpen && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                        {projects.map((project) => (
                          <div 
                            key={project.id}
                            onClick={() => {
                              setSelectedProject(project.id);
                              setIsProjectDropdownOpen(false);
                            }}
                            className="px-4 py-2 hover:bg-primary-50 cursor-pointer text-sm border-b border-gray-100 last:border-b-0"
                          >
                            {project.title}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <AlignLeft className="h-4 w-4 mr-2 text-gray-500" />
                    Beschreibung (optional)
                  </label>
                  <input
                    type="text"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    placeholder="Was wird erledigt?"
                  />
                </div>
                
                <div className="mb-5">
                  <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                    <Users className="h-4 w-4 mr-2 text-gray-500" />
                    Anzahl Mitarbeiter
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={employeeCount}
                    onChange={(e) => setEmployeeCount(parseInt(e.target.value) || 1)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  />
                </div>
                
                <button
                  onClick={startTracking}
                  disabled={!selectedProject}
                  className={`w-full px-4 py-3 rounded-md shadow-sm flex items-center justify-center focus-ring ${
                    !selectedProject 
                      ? 'bg-gray-200 text-gray-500 cursor-not-allowed' 
                      : 'bg-primary-600 hover:bg-primary-700 text-white transition-colors'
                  }`}
                >
                  <Play className="h-4 w-4 mr-2" fill="currentColor" />
                  Zeiterfassung starten
                </button>
              </div>
            )}
          </div>

          {/* Zeiterfassungen Bereich */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-5 pb-3 border-b border-gray-100 gap-3 sm:gap-0">
              <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                <Clock4 className="h-5 w-5 mr-2 text-primary-600" />
                Zeiterfassungen
              </h2>
              <div className="relative w-full sm:w-auto">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Filter className="h-5 w-5 text-gray-500" />
                  </div>
                  <select
                    value={selectedProject}
                    onChange={(e) => setSelectedProject(e.target.value)}
                    className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  >
                    <option value="">Alle Aufträge</option>
                    {projects.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.title}
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                    <ChevronDown className="h-5 w-5 text-gray-500" />
                  </div>
                </div>
              </div>
            </div>
            
            {filteredTimeEntries.length === 0 ? (
              <div className="text-center py-12 text-gray-500 border border-dashed border-gray-200 rounded-lg animate-fadeIn">
                <Clock className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500 font-medium">Keine Zeiterfassungen vorhanden</p>
                <p className="text-sm text-gray-400 mt-1">Starte eine Zeiterfassung oder trage einen manuellen Eintrag ein.</p>
              </div>
            ) : (
              <div className="overflow-x-auto -mx-6 px-6 custom-scrollbar">
                <table className="w-full modern-table">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Datum</th>
                      <th className="hidden sm:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Auftrag</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Zeit</th>
                      <th className="hidden md:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Beschreibung</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">MA</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Anfahrt</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Aktion</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredTimeEntries.map((entry, index) => (
                      <tr 
                        key={entry.id} 
                        className={`hover:bg-gray-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                        style={{animationDelay: `${index * 0.05}s`}}
                      >
                        <td className="px-4 py-3.5 text-sm text-gray-900 whitespace-nowrap">
                          <div className="font-medium">{formatDate(entry.start_time)}</div>
                          <div className="sm:hidden text-xs text-gray-700 mt-1">
                            {entry.project_title || 'Auftrag fehlt'}
                          </div>
                          {entry.description && (
                            <div className="sm:hidden text-xs text-gray-500 mt-1 line-clamp-1">
                              {entry.description}
                            </div>
                          )}
                        </td>
                        <td className="hidden sm:table-cell px-4 py-3.5 text-sm text-gray-900">
                          <div className="line-clamp-2 font-medium">{entry.project_title || 'Auftrag fehlt'}</div>
                        </td>
                        <td className="px-4 py-3.5 text-sm text-gray-900 whitespace-nowrap">
                          <div className="font-medium">{formatTime(entry.start_time)}</div>
                          <div className="text-gray-500">
                            {entry.end_time ? formatTime(entry.end_time) : 
                              <span className="badge badge-active flex items-center">
                                <span className="w-1.5 h-1.5 bg-green-500 rounded-full mr-1 animate-pulse"></span>
                                Aktiv
                              </span>
                            }
                          </div>
                        </td>
                        <td className="hidden md:table-cell px-4 py-3.5 text-sm text-gray-900">
                          <div className="line-clamp-2">{entry.description || '-'}</div>
                        </td>
                        <td className="px-4 py-3.5 text-sm text-gray-900">
                          <div className="flex items-center gap-1">
                            <Users className="h-3.5 w-3.5 text-gray-500" />
                            <span>{entry.employee_count || 1}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3.5 text-sm text-gray-900">
                          {parseFloat(entry.travel_allowance) > 0 ? (
                            <div className="flex items-center gap-1 text-primary-600">
                              <Car className="h-3.5 w-3.5" />
                              <span className="font-medium">CHF {parseFloat(entry.travel_allowance).toFixed(2)}</span>
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3.5 text-sm text-center">
                          {entry.end_time && (
                            <button 
                              onClick={() => handleEditEntry(entry)} 
                              className="text-primary-600 hover:text-primary-700 p-1.5 rounded-full hover:bg-primary-50 transition-colors focus-ring"
                              title="Bearbeiten"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimeTrackingPage;
